import React, { memo, useCallback, useState } from "react";
import { Avatar, IskoolLogoEyes } from "@/components/atoms";
import { IconButton } from "@/components/atoms/IconButton";
import { Bell, Menu, Sun, Moon, PanelLeftClose, PanelLeft, Settings, Power, Coins } from "lucide-react";
import { cn } from "@/lib/utils";
import { HeaderSearch } from "@/components/molecules/HeaderSearch";
import { NotificationDropdown } from "@/components/organisms/NotificationDropdown";
import { NotificationBadge } from "@/components/atoms/NotificationBadge";
import { useUnreadNotificationCount } from "@/hooks/useNotifications";
import { useAuth } from "@/hooks/useAuth";
import { useCreditBalance } from "@/hooks/useCredits";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { useTheme } from "next-themes";
import { useSidebarContext } from '@/contexts/useSidebarContext';
import { ConfirmationModal } from "@/components/molecules/ConfirmationModal";

interface MenuItemProps {
  icon: React.ReactNode;
  label: string;
  description: string;
  onClick: () => void;
  variant?: 'default' | 'destructive';
}

const MenuItem = memo<MenuItemProps>(function MenuItem({ icon, label, description, onClick, variant = 'default' }) {
  const isDestructive = variant === 'destructive';
  return (
    <button
      onClick={onClick}
      className={cn(
        "w-full flex items-start gap-3 px-3 py-2.5 rounded-lg transition-colors cursor-pointer hover:bg-accent/50 focus:outline-none focus:bg-accent/50",
        isDestructive && "text-destructive"
      )}
    >
      <div className="w-5 h-5 mt-0.5 shrink-0">{icon}</div>
      <div className="flex-1 text-left">
        <p className="text-sm font-medium">{label}</p>
        <p className={cn("text-xs", isDestructive ? "text-destructive/80" : "text-muted-foreground")}>{description}</p>
      </div>
    </button>
  );
});

interface HeaderContainerProps {
  firstName?: string | null;
  lastName?: string | null;
  username?: string | null;
  avatarUrl?: string | null;
  onAvatarClick?: () => void;
  onSettingsClick?: () => void;
  onSignOut?: () => void;
  className?: string;
  sidebarWidth?: number;
}

export const HeaderContainer = memo<HeaderContainerProps>(function HeaderContainer({ 
  firstName,
  lastName, 
  username,
  avatarUrl,
  onAvatarClick,
  onSettingsClick,
  onSignOut,
  className,
  sidebarWidth = 256
}) {
  const [showSignOutConfirm, setShowSignOutConfirm] = useState(false);
  const { data: unreadCount = 0 } = useUnreadNotificationCount();
  const { theme, setTheme } = useTheme();
  const { isCollapsed, toggleSidebar } = useSidebarContext();
  const { user } = useAuth();
  const { data: creditData, isLoading: creditsLoading } = useCreditBalance(user?.id ?? '');
  const availableCredits = creditData?.available ?? 0;

  const handleThemeToggle = useCallback(() => {
    setTheme(theme === 'dark' ? 'light' : 'dark');
  }, [theme, setTheme]);

  return (
    <div
      data-component="dashboard-header"
      className={cn("fixed top-6 z-50 flex justify-center px-4", className)}
      style={{
        left: 0,
        right: 0,
        width: '100vw'
      }}
    >
      {/* Eye Logo - aligned above sidebar */}
      <div className="absolute left-6 top-1/2 -translate-y-1/2">
        <IskoolLogoEyes className="h-10 w-auto" />
      </div>

      {/* Menu Popover - aligned to the right.
          NOTE: PopoverTrigger asChild forwards Radix's button ARIA attrs
          (aria-haspopup, aria-expanded, aria-controls, type="button") to its
          IMMEDIATE child. We drop the previous wrapping <div> so Radix
          forwards directly to IconButton's real <button> — putting
          aria-expanded on a div was an axe `critical` (Step 43.A audit). */}
      <Popover>
        <PopoverTrigger asChild>
          <IconButton
            size="sm"
            disableHoverScale={true}
            aria-label="Open account menu"
            className="absolute right-6 top-1/2 -translate-y-1/2"
          >
            <Menu className="h-6 w-6 text-foreground" />
          </IconButton>
        </PopoverTrigger>
        <PopoverContent align="end" className="w-64 bg-card border border-border p-2" onOpenAutoFocus={(e) => e.preventDefault()}>
          {user?.id && (
            <div className="mb-1 pb-1 border-b border-border">
              <div className="w-full flex items-start gap-3 px-3 py-2.5 rounded-lg">
                <div className="w-5 h-5 mt-0.5 shrink-0"><Coins className="h-5 w-5" /></div>
                <div className="flex-1 text-left">
                  <p className="text-sm font-medium">Credits</p>
                  <p className="text-xs text-muted-foreground">
                    {creditsLoading ? 'Loading balance…' : `${availableCredits.toLocaleString()} available`}
                  </p>
                </div>
              </div>
            </div>
          )}
          {onSettingsClick && (
            <MenuItem
              icon={<Settings className="h-5 w-5" />}
              label="Settings"
              description="Manage your preferences"
              onClick={onSettingsClick}
            />
          )}
          <div className="mt-1 pt-1 border-t border-border">
            <MenuItem
              icon={theme === 'dark' ? <Sun className="h-5 w-5" /> : <Moon className="h-5 w-5" />}
              label={theme === 'dark' ? 'Light Mode' : 'Dark Mode'}
              description={theme === 'dark' ? 'Switch to light theme' : 'Switch to dark theme'}
              onClick={handleThemeToggle}
            />
            <MenuItem
              icon={isCollapsed ? <PanelLeft className="h-5 w-5" /> : <PanelLeftClose className="h-5 w-5" />}
              label={isCollapsed ? 'Expand Sidebar' : 'Collapse Sidebar'}
              description={isCollapsed ? 'Show full navigation labels' : 'Minimise to icon-only view'}
              onClick={toggleSidebar}
            />
          </div>
          {onSignOut && (
            <div className="mt-3 pt-3 border-t border-border">
              <MenuItem
                icon={<Power className="h-5 w-5" />}
                label="Sign Out"
                description="Log out of your account"
                onClick={() => setShowSignOutConfirm(true)}
                variant="destructive"
              />
            </div>
          )}
        </PopoverContent>
      </Popover>

      {onSignOut && (
        <ConfirmationModal
          open={showSignOutConfirm}
          onOpenChange={setShowSignOutConfirm}
          title="Sign Out"
          description="Are you sure you want to sign out of your account?"
          confirmText="Sign Out"
          variant="destructive"
          onConfirm={onSignOut}
        />
      )}

      <div className="flex gap-3 w-full max-w-lg">
        {/* Avatar */}
        <Avatar 
          src={avatarUrl}
          firstName={firstName}
          lastName={lastName}
          username={username}
          onClick={onAvatarClick}
          alt="User avatar"
        />

        {/* Search Bar */}
        <HeaderSearch />

        {/* Notifications Dropdown */}
        <NotificationDropdown>
          <IconButton
            size="sm"
            disableHoverScale={true}
            aria-label={unreadCount > 0 ? `Notifications, ${unreadCount} unread` : 'Notifications'}
          >
            <div className="relative">
              <Bell className="h-6 w-6 text-foreground" />
              <NotificationBadge count={unreadCount} />
            </div>
          </IconButton>
        </NotificationDropdown>
      </div>
    </div>
  );
});